// Cross-product line model — pure, DOM-free, so it can be unit-tested.
//
// The chart draws one line per distinct tuple of the *line-capable* dimensions
// (every dim NOT marked `single_select` in the meta). The API already returns
// the full cross-product long frame for the selected dims; this module groups
// that frame into lines and assigns each a visual channel:
//   0 varying dims -> a single line (one colour)
//   1 varying dim  -> colour encodes that dim
//   2 varying dims -> colour encodes the 1st (in dim_order), dash the 2nd
//   3+ varying dims -> no clean third channel: colour per tuple, no dash
// "Varying" = a line dim with >1 distinct level across the drawn lines. A line
// dim pinned to one level is constant, so it consumes no channel and is dropped
// from the legend label.

const DELIM = "";

export interface LongFrame {
  date: string[];
  value: (number | null)[];
  [dim: string]: any;
}

export interface RawLine {
  tuple: Record<string, string>;
  dates: string[];
  values: (number | null)[];
}

// Group a long frame into one series per distinct tuple of the line dims that
// are actually present as columns in the frame. Rows whose line-dim value falls
// outside the current selection are dropped (defensive — the API already
// filters by the dims param). Tuple order follows first appearance in the frame.
export function groupLines(
  lineDimsInOrder: string[],
  selection: Record<string, string[]>,
  frame: LongFrame,
): RawLine[] {
  const lds = lineDimsInOrder.filter(d => Array.isArray(frame[d]));
  const n = frame.date ? frame.date.length : 0;
  if (!lds.length || !n) return [];
  const groups = new Map<string, RawLine>();
  for (let r = 0; r < n; r++) {
    const dt = frame.date[r];
    if (dt == null) continue;
    if (lds.some(d => {
      const sel = selection[d];
      return sel && sel.length && !sel.includes(frame[d][r]);
    })) continue;
    const tuple: Record<string, string> = {};
    for (const d of lds) tuple[d] = frame[d][r];
    const key = lds.map(d => tuple[d]).join(DELIM);
    let g = groups.get(key);
    if (!g) { g = { tuple, dates: [], values: [] }; groups.set(key, g); }
    g.dates.push(dt);
    g.values.push(frame.value[r] ?? null);
  }
  return [...groups.values()].filter(g => g.dates.length > 0);
}

// Which line dims vary across the drawn lines, in the given (dim_order) order —
// these are the channel-carrying dims. Computed from the *drawn* tuples (not the
// selection) so a dim left constant by empty-cell skipping carries no channel.
export function varyingOf(
  lineDimsInOrder: string[],
  lines: { tuple: Record<string, string> }[],
): string[] {
  return lineDimsInOrder.filter(d => {
    const seen = new Set<string>();
    for (const ln of lines) { const v = ln.tuple[d]; if (v != null) seen.add(v); }
    return seen.size > 1;
  });
}

export interface Channels { colorIdx: number; dashIdx: number; }

// Channel assignment for one tuple. Colour/dash indices are positions within the
// user's selection order for that dim, so a level keeps its colour as siblings
// are added/removed. 3+ varying dims fall back to a per-line colour index.
export function channelsFor(
  tuple: Record<string, string>,
  lineIdx: number,
  varyDims: string[],
  selection: Record<string, string[]>,
): Channels {
  if (varyDims.length >= 3) return { colorIdx: lineIdx, dashIdx: 0 };
  const idxIn = (dim: string) => {
    const sel = selection[dim] || [];
    const v = tuple[dim];
    const i = v == null ? -1 : sel.indexOf(v);
    return i < 0 ? 0 : i;
  };
  return {
    colorIdx: varyDims[0] ? idxIn(varyDims[0]) : 0,
    dashIdx: varyDims[1] ? idxIn(varyDims[1]) : 0,
  };
}

// Legend / tooltip label: the varying dims' level labels joined by " · ". When
// nothing varies (a single line) use the supplied fallback (the chart title).
export function lineLabel(
  tuple: Record<string, string>,
  varyDims: string[],
  levelLabel: (dim: string, code: string) => string,
  fallback: string,
): string {
  if (!varyDims.length) return fallback;
  return varyDims.map(d => levelLabel(d, tuple[d] ?? "")).join(" · ");
}

// The selected combinations of the line dims that drew no line: the chart draws
// the cross-product of the selection and silently skips cells without data
// (bond yields: cantons have no 1-year series), so a picked series could simply
// not appear. The legend names these instead. Empty when the cross-product is
// larger than `cap`, where listing cells would not help anyone.
export function missingTuples(
  lineDimsInOrder: string[],
  selection: Record<string, string[]>,
  drawn: { tuple: Record<string, string> }[],
  cap = 200,
): Record<string, string>[] {
  const dims = lineDimsInOrder.filter(d => (selection[d] || []).length);
  if (!dims.length) return [];
  const size = dims.reduce((n, d) => n * (selection[d] as string[]).length, 1);
  if (size > cap) return [];
  const key = (t: Record<string, string>) => dims.map(d => String(t[d])).join(DELIM);
  const have = new Set(drawn.map(l => key(l.tuple)));
  let cells: Record<string, string>[] = [{}];
  for (const d of dims)
    cells = cells.flatMap(c => (selection[d] as string[]).map(code => ({ ...c, [d]: code })));
  return cells.filter(c => !have.has(key(c)));
}
